import { PublicPageShell } from '@/components/PublicPageShell';
import type { Lang } from './LandingPage';

interface ChangelogPageProps {
  lang: Lang;
  setLang: (lang: Lang) => void;
  onBack: () => void;
}

const content = {
  zh: {
    title: '更新日志',
    intro: 'TingYue 的持续迭代记录。',
    versions: [
      {
        version: 'v1.3.0',
        date: '2026-06-16',
        items: [
          '数据库连接复用（Node.js + Python）与 WAL 模式',
          '真多用户 T-Engine，按用户隔离内存状态与广播',
          '实时 WebSocket 通知推送与价格预警',
          'AI 记忆与会话上下文持久化',
          '组合净值曲线基于真实历史 K 线计算',
          '高级选股字段：市值、PE、PB、ROE、营收增速、股息率',
          '性能优化：懒加载、骨架屏、更小首屏包',
          '移动端适配与 MobileNav',
          '前端测试从 11 个扩展到 38 个',
          '新增微信扫码登录骨架',
          '全新中英文官网首页',
        ],
      },
      {
        version: 'v1.2.0',
        date: '2026-06-16',
        items: [
          '新增 Caddy 反代与 Docker Compose 部署',
          'AI 共识面板与多模型投票',
          'LLM 配置独立加密存储',
          '通知中心与价格预警雏形',
        ],
      },
      {
        version: 'v1.1.0',
        date: '2026-06',
        items: [
          '组合管理多持仓支持',
          '资金流向与自选股行情',
          '用户现金与费用计算',
        ],
      },
      {
        version: 'v1.0.0',
        date: '2026-06',
        items: ['项目初始版本，包含交易记录、做T分析与基础统计'],
      },
    ],
  },
  en: {
    title: 'Changelog',
    intro: 'The ongoing iteration log of TingYue.',
    versions: [
      {
        version: 'v1.3.0',
        date: '2026-06-16',
        items: [
          'Shared SQLite connection (Node.js + Python) with WAL mode',
          'True multi-user T-Engine with per-user state and broadcasts',
          'Real-time WebSocket notifications and price alerts',
          'AI memory and persistent conversation context',
          'Real portfolio NAV curve from historical K-lines',
          'Advanced screener fields: market cap, PE, PB, ROE, revenue growth, dividend yield',
          'Performance optimizations: lazy loading, skeleton screens, smaller initial bundle',
          'Mobile adaptation with MobileNav',
          'Frontend tests expanded from 11 to 38 cases',
          'WeChat QR login skeleton',
          'New bilingual landing page',
        ],
      },
      {
        version: 'v1.2.0',
        date: '2026-06-16',
        items: [
          'Caddy reverse proxy and Docker Compose deployment',
          'AI consensus panel and multi-model voting',
          'Encrypted per-user LLM configuration storage',
          'Notification center and price alert prototype',
        ],
      },
      {
        version: 'v1.1.0',
        date: '2026-06',
        items: [
          'Multi-holding portfolio management',
          'Fund flow and watchlist quotes',
          'User cash and fee calculation',
        ],
      },
      {
        version: 'v1.0.0',
        date: '2026-06',
        items: ['Initial release with trade records, T-trade analytics and basic statistics'],
      },
    ],
  },
};

export function ChangelogPage({ lang, setLang, onBack }: ChangelogPageProps) {
  const c = content[lang];
  return (
    <PublicPageShell title={c.title} lang={lang} setLang={setLang} onBack={onBack}>
      <p className="text-muted-foreground mb-8">{c.intro}</p>
      <div className="space-y-8">
        {c.versions.map((v) => (
          <div key={v.version} className="border-l-2 border-[#533afd] pl-6">
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-xl font-semibold">{v.version}</h2>
              <span className="text-sm text-muted-foreground">{v.date}</span>
            </div>
            <ul className="list-disc list-inside space-y-1.5 text-muted-foreground">
              {v.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </PublicPageShell>
  );
}
